import axios, { isAxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { API_HOST, CAMPAIGN_STATUS_REVERSE_PROXY_URI, USE_FLEET_PROD } from '../../config/EnviromentConfig';
import { getToken } from '../../config/MsalAuthConfig';

import type { IFleetAccountResponse } from './types/IFleetAccount';
import type { IUserAuthorizationsResponse } from './types/IUserAuthorizations';
import type { IServicesFormData } from '@/components/modals/digital-service/DigitalServiceModal';
import type {
  TRegisterDeviceRequest,
  TRegisterDeviceResponse,
} from '@/components/remote-software/types/register-device';
import type {
  ActiveFaultCodeItem,
  ChassisActiveStatusLog,
  IActiveStateLatestJson,
} from '@/service/vpc/types/IActiveStateLatestJson';
import type {
  IAftermarketCampaignResponse,
  ICampaignWithSerialNumbers,
} from '@/service/vpc/types/IAftermarketCampaign';
import type { IApiResponse } from '@/service/vpc/types/IApiResponse';
import type { AssetPositionQueryParams, IAssetPositionResponse } from '@/service/vpc/types/IAssetPositionResponse';
import type { IGeocodingResponse } from '@/service/vpc/types/IAutocompleteResponse';
import type { IFuelConsumptionResponse } from '@/service/vpc/types/IChassisFuelConsumption';
import type { IConnectivityStatus } from '@/service/vpc/types/IConnectivityStatus';
import type { ICreateUnitMetadata, IUpdateUnitMetadata } from '@/service/vpc/types/ICreateUnitMetadata';
import type { IEndCustomer } from '@/service/vpc/types/IEndCustomer';
import type { ICommunicationPreference, IEndUser } from '@/service/vpc/types/IEndUser';
import type { IFleet } from '@/service/vpc/types/IFleet';
import type { IFleetAccountRole } from '@/service/vpc/types/IFleetAccountRole';
import type { IFleetInvitationResponse, InvitationStatusType } from '@/service/vpc/types/IFleetInvitations';
import type { IFleetMember } from '@/service/vpc/types/IFleetMember';
import type { IFleetRole, IFleetRolesResponse } from '@/service/vpc/types/IFleetRoles';
import type { IFleetUnit, IFleetUnitsResponse } from '@/service/vpc/types/IFleetUnit';
import type { IIndividualProductsResponse } from '@/service/vpc/types/IIndividualProducts';
import type { IInstallableCampaign } from '@/service/vpc/types/IInstallableCampaign';
import type { IAcceptInvitationRequestBody, IInvitation } from '@/service/vpc/types/IInvitations';
import type { ILastSeen } from '@/service/vpc/types/ILastSeen';
import type { ILatestOperatingHours } from '@/service/vpc/types/ILatestOperatingHours';
import type { IPlaceDetailsResponse } from '@/service/vpc/types/IPlaceDetailsResponse';
import type { IPublicationResponse } from '@/service/vpc/types/IPublication';
import type { IPublicationMetadata } from '@/service/vpc/types/IPublicationMetadata';
import type { IServiceProtocol, IServiceProtocolResponse } from '@/service/vpc/types/IServiceProtocol';
import type { ITelemetryBatchServiceResponse } from '@/service/vpc/types/ITelemetryBatchService';
import type { ITelemetryServiceResponse } from '@/service/vpc/types/ITelemetryService';
import type { IChassisIdentifier, IUnit } from '@/service/vpc/types/IUnit';
import type { IUnitBySerialNumberResponse } from '@/service/vpc/types/IUnitBySerialNumber';
import type { IUnitMetadataResponse } from '@/service/vpc/types/IUnitMetaData';
import type { IUnitRegistration } from '@/service/vpc/types/IUnitRegistration';
import type {
  IWifiConfigUpdateStatusResponse,
  IWifiConfigUpdateStatusSimple,
  IWifiConfigurationRequest,
  IWifiErrorHistoryResponse,
  IWifiNetworkRow,
  IWifiStatusResponseAndCorrelationId,
} from '@/service/vpc/types/IWifiConfiguration';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { assetPositions, common, dates } from '@/utils';

interface Error {
  message: string;
  extensions: Extensions;
}

interface Extensions {
  code: string;
  httpStatus: number;
}

// Legal contract documents are served on anonymous, canonically named routes
// (…/document/Terms-And-Conditions-UK.pdf) so plain navigation shows the document
// name in the browser URL and save dialog (AB#1540370).
export const legalContractDocumentUrl = (uri: string): string => `${API_HOST.replace(/\/+$/, '')}/issb/${uri}`;

export const client = axios.create({
  baseURL: API_HOST,
  timeout: 1000 * 60 * 10,
  headers: {
    'Cache-Control': 'no-cache',
  },
});

// Fleet API writes respond before they are readable, so we wait this long
// after a write (and between retries) before relying on the result.
const FLEET_API_CONSISTENCY_DELAY_MS = 700;
// How many times we re-read a fleet's units before giving up on seeing a change
const FLEET_API_MAX_CONSISTENCY_CHECKS = 10;
const FLEET_API_WRITE_ATTEMPTS = 3;

/**
 * Re-reads the fleet's units until `isDone` returns true, so a refetch afterwards
 * shows the change. Gives up quietly after FLEET_API_MAX_CONSISTENCY_CHECKS reads.
 */
const waitForFleetUnits = async (
  fleetId: string,
  isDone: (unitIds: Set<string>) => boolean,
  signal?: AbortSignal
): Promise<void> => {
  for (let check = 1; check <= FLEET_API_MAX_CONSISTENCY_CHECKS; check++) {
    await common.wait(FLEET_API_CONSISTENCY_DELAY_MS);
    try {
      const { items } = await vpcService.Fleet.getFleetUnits({ fleetId, signal });
      if (isDone(new Set(items.map((unit) => String(unit.endCustomerUnitId))))) return;
    } catch {
      // Keep checking; the read can fail while the write is still propagating
    }
  }
  console.warn(`Fleet ${fleetId}: units change not visible after ${FLEET_API_MAX_CONSISTENCY_CHECKS} checks`);
};

// A successful delete can take up to ~20 s to show up in the Fleet API's reads. We remember
// what we removed and hide it from every read until then, so refetches don't bring it back.
const PENDING_DELETE_TTL_MS = 30 * 1000;
const pendingUnitRemovals = new Map<string, Map<string, number>>(); // fleetId -> unitId -> expiresAt
const pendingFleetDeletions = new Map<string, number>(); // fleetId -> expiresAt

const removeExpired = (entries: Map<string, number>) => {
  const now = Date.now();
  entries.forEach((expiresAt, id) => {
    if (expiresAt <= now) entries.delete(id);
  });
};

const getPendingUnitRemovals = (fleetId: string): Set<string> => {
  const units = pendingUnitRemovals.get(fleetId);
  if (!units) return new Set();
  removeExpired(units);
  return new Set(units.keys());
};

const markUnitsRemoved = (fleetId: string, unitIds: string[]) => {
  const units = pendingUnitRemovals.get(fleetId) ?? new Map<string, number>();
  unitIds.forEach((id) => units.set(id, Date.now() + PENDING_DELETE_TTL_MS));
  pendingUnitRemovals.set(fleetId, units);
};

// A unit added back to the fleet must show up again
const unmarkUnitsRemoved = (fleetId: string, unitIds: string[]) => {
  unitIds.forEach((id) => pendingUnitRemovals.get(fleetId)?.delete(id));
};

const isFleetPendingDeletion = (fleetId: string): boolean => {
  removeExpired(pendingFleetDeletions);
  return pendingFleetDeletions.has(fleetId);
};

const withoutPendingUnitRemovals = (fleetId: string, response: IFleetUnitsResponse): IFleetUnitsResponse => {
  const pending = getPendingUnitRemovals(fleetId);
  if (pending.size === 0) return response;
  const items = response.items.filter((unit) => !pending.has(String(unit.endCustomerUnitId)));
  return { ...response, items, totalItems: response.totalItems - (response.items.length - items.length) };
};

const logFailedUnits = (
  action: string,
  fleetId: string,
  unitIds: string[],
  results: PromiseSettledResult<unknown>[]
) => {
  results.forEach((result, i) => {
    if (result.status === 'rejected') {
      console.error(`Failed to ${action} unit ${unitIds[i]} (fleet ${fleetId})`, result.reason);
    }
  });
};

const getFleetApiUrl = (path: string, version: string = 'v1'): string => {
  const prefix = USE_FLEET_PROD ? `bdip/prod/fleets-api/api/${version}` : `bdip/fleets-api/api/${version}`;
  return `${prefix}/${path}`;
};

const baseApiCall = async <T>(url: string, config?: AxiosRequestConfig, includeHeaders?: boolean): Promise<T> => {
  // instread of get the token from MSAL here i can take the token from the url params
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token') || (await getToken());

  try {
    const response = await client<T>(url, {
      ...config,
      headers: { Authorization: `Bearer ${token}` },
    });

    return includeHeaders ? (response as T) : response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError<{ errors: Error[] }>;
      if (axiosError.response && axiosError.response.data) {
        throw axiosError.response.data; // Throw the server's error payload
      }
    }
    throw error; // Re-throw other errors
  }
};

export const vpcService = {
  FleetAccount: {
    updateFleetAccount: async ({
      fleetAccountId,
      name,
      entityVersion,
      signal,
    }: {
      fleetAccountId: string;
      name: string;
      entityVersion: number;
      signal?: AbortSignal;
    }) => {
      const url = getFleetApiUrl(`fleet-accounts/${fleetAccountId}`);
      const response = await baseApiCall<number>(url, {
        method: 'PUT',
        data: { name, entityVersion, isActive: true },
        signal,
      });
      return response;
    },
    getEndUser: async ({ signal }: { signal?: AbortSignal } = {}) => {
      const url = getFleetApiUrl('end-users/me');
      const response = await baseApiCall<IEndUser>(url, { signal });
      return response;
    },
    addEndUserCommunicationPreference: async ({
      endUserId,
      preference,
      entityVersion,
      signal,
    }: {
      endUserId: string;
      preference: ICommunicationPreference;
      entityVersion: number;
      signal?: AbortSignal;
    }) => {
      const url = getFleetApiUrl(`end-users/${endUserId}/communication-preferences`);
      const response = await baseApiCall<number>(url, {
        method: 'POST',
        data: {
          CommunicationPreferences: [preference],
          EntityVersion: entityVersion,
        },
        signal,
      });
      return response;
    },
    removeEndUserCommunicationPreference: async ({
      endUserId,
      preference,
      entityVersion,
      signal,
    }: {
      endUserId: string;
      preference: ICommunicationPreference;
      entityVersion: number;
      signal?: AbortSignal;
    }) => {
      const url = getFleetApiUrl(
        `end-users/${endUserId}/communication-preferences?communicationPreference=${preference}&entityVersion=${entityVersion}`
      );
      const response = await baseApiCall<number>(url, {
        method: 'DELETE',
        signal,
      });
      return response;
    },
    getUserAuthorizations: async ({ signal, clearCache }: { signal?: AbortSignal; clearCache?: boolean } = {}) => {
      const url = clearCache ? `user-authorizations-v2?clearCache=true` : `user-authorizations-v2`;
      const response = await baseApiCall<IUserAuthorizationsResponse>(url, { signal });
      return response;
    },
    getFleetAccounts: async ({ signal }: { signal?: AbortSignal } = {}) => {
      const url = getFleetApiUrl('fleet-accounts');
      const response = await baseApiCall<IFleetAccountResponse>(url, { signal });
      return response;
    },
    getFleetAccountEndUsers: async ({ fleetAccountId, signal }: { fleetAccountId: string; signal?: AbortSignal }) => {
      const url = getFleetApiUrl(`fleet-accounts/${fleetAccountId}/end-users`);
      const response = await baseApiCall<IFleetMember[]>(url, { signal });
      return response;
    },
    getFleetAccountEndUserById: async ({
      fleetAccountId,
      endUserId,
      signal,
    }: {
      fleetAccountId: string;
      endUserId: string;
      signal?: AbortSignal;
    }) => {
      const url = getFleetApiUrl(`fleet-accounts/${fleetAccountId}/end-users`);
      const response = await baseApiCall<IFleetMember[]>(url, { signal });
      const endUser = response.find((user) => user.endUserId === endUserId);
      return endUser;
    },

    disconnectEndUser: async ({
      fleetAccountId,
      members,
      signal,
    }: {
      fleetAccountId: string;
      members: IFleetMember[];
      signal?: AbortSignal;
    }) => {
      const promises = members.map((member) => {
        const url = getFleetApiUrl(`fleet-accounts/${fleetAccountId}/disconnect-end-user`);
        const response = baseApiCall<IFleetMember[]>(url, {
          method: 'POST',
          data: {
            entityVersion: member.entityVersion,
            endUserId: member.endUserId,
          },
          signal,
        });
        return response;
      });
      const results = await Promise.allSettled(promises);
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result) => (result as PromiseFulfilledResult<IFleetMember[]>).value);
      return successfulResponses;
    },
    getAvailableRoles: async ({ signal }: { signal?: AbortSignal } = {}) => {
      const url = getFleetApiUrl(`fleet-accounts/roles`);
      const response = await baseApiCall<string[]>(url, { signal });
      return response;
    },
    getFleetAccountRoles: async ({ fleetAccountId, signal }: { fleetAccountId: string; signal?: AbortSignal }) => {
      const url = getFleetApiUrl(`fleet-accounts/${fleetAccountId}/roles`);
      const response = await baseApiCall<IFleetAccountRole[]>(url, { signal });
      return response;
    },
    assignFleetAccountRoles: async ({
      fleetAccountId,
      endUserId,
      roles,
      signal,
    }: {
      fleetAccountId: string;
      endUserId: string;
      roles: string[];
      signal?: AbortSignal;
    }) => {
      const promises = roles.map((role) => {
        const url = getFleetApiUrl(`fleet-accounts/${fleetAccountId}/roles/${uuidv4()}`);
        return baseApiCall<IFleetMember[]>(url, {
          method: 'POST',
          data: { endCustomerId: endUserId, role },
          signal,
        });
      });
      const results = await Promise.allSettled(promises);
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result) => (result as PromiseFulfilledResult<IFleetMember[]>).value);
      return successfulResponses;
    },
    removeFleetAccountRoles: async ({
      fleetAccountId,
      roles,
      signal,
    }: {
      fleetAccountId: string;
      roles: IFleetAccountRole[];
      signal?: AbortSignal;
    }) => {
      const promises = roles.map((role) => {
        const url = getFleetApiUrl(
          `fleet-accounts/${fleetAccountId}/roles/${role.fleetAccountRoleId}?entityVersion=${role.entityVersion}`
        );
        return baseApiCall<IFleetMember[]>(url, {
          method: 'DELETE',
          signal,
        });
      });
      const results = await Promise.allSettled(promises);
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result) => (result as PromiseFulfilledResult<IFleetMember[]>).value);
      return successfulResponses;
    },
  },
  Units: {
    getUnitsWithFBB: async ({ signal }: { signal?: AbortSignal } = {}) => {
      const url = `units-v3?withFBB=true`;
      const response = await baseApiCall<IUnit[]>(url, { signal });
      return response;
    },
    getUnits: async ({ signal }: { signal?: AbortSignal } = {}) => {
      const url = `units`;
      const response = await baseApiCall<IUnit[]>(url, { signal });
      return response;
    },
    getUnitById: async ({ endCustomerUnitId, signal }: { endCustomerUnitId: string; signal?: AbortSignal }) => {
      const url = `unit-v3/${endCustomerUnitId}`;
      const response = await baseApiCall<IApiResponse<IUnit>>(url, { signal });
      if (!response.isSuccess || !response.data) {
        const message = response.errors?.map((e) => e.message).join(', ') || 'Failed to fetch unit';
        throw new Error(message);
      }
      return response.data;
    },
    getUnitBySerialNumber: async ({
      serialNumber,
      fleetAccountId,
      signal,
    }: {
      serialNumber: string;
      fleetAccountId: string;
      signal?: AbortSignal;
    }) => {
      const url = `unit-by-serial-v2/${serialNumber}?slim=true&faid=${fleetAccountId}`;
      const response = await baseApiCall<IUnitBySerialNumberResponse>(url, { signal });
      return response.data;
    },
    registerUnit: async ({ body, signal }: { body: IUnitRegistration; signal?: AbortSignal }) => {
      const url = `unit-registration/v2`;
      const formData = new FormData();
      if ('ownershipFiles' in body) {
        const { ownershipFiles, ...rest } = body;
        formData.append('data', JSON.stringify(rest));
        ownershipFiles.forEach((fui) => formData.append('files', fui.file, fui.file.name));
      } else {
        formData.append('data', JSON.stringify(body));
      }
      // unit: the saved claim in the units-v2 shape (AB#1640822); absent from older proxies.
      return baseApiCall<{ unitId: number; unit?: IUnit | null }>(url, {
        method: 'POST',
        data: formData,
        signal,
      });
    },
    addUnitsMetadata: async ({
      metadata,
      image,
      signal,
    }: {
      metadata: ICreateUnitMetadata;
      image?: File;
      signal?: AbortSignal;
    }) => {
      const url = `metadata/unitmetadata/add`;
      const formData = new FormData();
      formData.append('metadata', JSON.stringify(metadata));
      if (image) {
        formData.append('image', image);
      }
      const response = await baseApiCall<IUnitMetadataResponse>(url, {
        method: 'POST',
        data: formData,
        signal,
      });
      return response;
    },
    updateUnitsMetadata: async ({
      fleetAccountId,
      endCustomerUnitId,
      metadata,
      image,
      signal,
    }: {
      fleetAccountId: string;
      endCustomerUnitId: string;
      metadata: IUpdateUnitMetadata;
      image?: File;
      signal?: AbortSignal;
    }) => {
      const url = `metadata/unitmetadata/${fleetAccountId}/${endCustomerUnitId}`;
      const formData = new FormData();
      formData.append('metadata', JSON.stringify(metadata));
      if (image) {
        formData.append('image', image);
      }
      const response = await baseApiCall<IUnitMetadataResponse>(url, {
        method: 'PATCH',
        data: formData,
        signal,
      });
      return response;
    },
    deleteAvatar: async ({
      fleetAccountId,
      endCustomerUnitId,
      signal,
    }: {
      fleetAccountId: string;
      endCustomerUnitId: string;
      signal?: AbortSignal;
    }) => {
      const url = `metadata/unitmetadata/${fleetAccountId}/${endCustomerUnitId}/avatar`;
      const response = await baseApiCall(url, {
        method: 'DELETE',
        signal,
      });
      return response;
    },
  },
  UnitStatuses: {
    getLatestActiveState: async ({
      chassisIds,
      pageSize,
      pageNumber,
      signal,
    }: {
      chassisIds: string;
      pageSize: number;
      pageNumber: number;
      signal?: AbortSignal;
    }) => {
      const query = `{activeStateLatestJson(chassisIds:"${chassisIds.trim()}",pageSize:${pageSize},pageNumber:${pageNumber})}`;
      const url = `bdip/unit-statuses-api/v1/graphql?query=${query}`;

      const response = await baseApiCall<{ data: { activeStateLatestJson: string } }>(url, {
        signal,
      });
      const data = JSON.parse(response.data.activeStateLatestJson) as IActiveStateLatestJson;

      const getAllFaults = (logs: ChassisActiveStatusLog[]) => {
        return (logs ?? []).flatMap((log) =>
          (log.ComponentDtcStatusFaults ?? []).map((fault) => ({
            ...fault,
            ReadoutdDateTime: log.ReadoutdDateTime,
            ReceivedDateTime: log.ReceivedDateTime,
            ChassisId: log.ChassisId,
          }))
        );
      };

      const listItems: ActiveFaultCodeItem[] = data?.UnitMetrics.map((row) => {
        const allFaults = getAllFaults(row.ChassisActiveStatusLogs);
        return allFaults;
      }).flat();

      return listItems;
    },
    getUnitsLastSeen: async ({
      endCustomerUnitIds,
      signal,
    }: {
      endCustomerUnitIds: (number | string)[];
      signal?: AbortSignal;
    }) => {
      const query = `{unitsLastSeen(endCustomerUnitIds:"${endCustomerUnitIds}")}`;
      const url = `bdip/unit-statuses-api/v1/graphql?query=${query}`;
      const response = await baseApiCall<{ data: { unitsLastSeen: string } }>(url, { signal });
      const data = JSON.parse(response.data.unitsLastSeen) as ILastSeen[];
      return data;
    },
    getUnitsConnectivityStatus: async ({
      endCustomerUnitIds,
      signal,
    }: {
      endCustomerUnitIds: (number | string)[];
      signal?: AbortSignal;
    }) => {
      const query = `{unitConnectivityStatusJson(unitIds:"${endCustomerUnitIds}")}`;
      const url = `bdip/unit-statuses-api/v1/graphql?query=${query}`;
      const response = await baseApiCall<{ data: { unitConnectivityStatusJson: string } }>(url, { signal });
      const data = JSON.parse(response.data.unitConnectivityStatusJson) as IConnectivityStatus[];
      return data;
    },
    // getDtcStatus: async ({ statusId, chassisId }: { statusId: number; chassisId: string }) => {
    //   const queryString = `{componentStatusGroupedReadoutJson(statusId:${statusId},chassisId:"${chassisId}")}`;
    //   const url = `bdip/unit-statuses-api/v1/graphql?query=${queryString}`;
    //   const response = await baseApiCall<{ data: { componentStatusGroupedReadoutJson: string } }>(
    //     url
    //   );
    //   const data = JSON.parse(
    //     response.data.componentStatusGroupedReadoutJson
    //   ) as IComponentDtcStatus;
    //   return data;
    // },
    // getDtcLog: async ({ dtcLogId, chassisId }: { dtcLogId: number; chassisId: string }) => {
    //   const queryString = `{dtcLogJson(chassisId:"${chassisId}",dtcLogId:${dtcLogId})}`;
    //   const url = `bdip/unit-statuses-api/v1/graphql?query=${queryString}`;
    //   const response = await baseApiCall<{ data: { dtcLogJson: string } }>(url);
    //   const data = JSON.parse(response.data.dtcLogJson) as IDtcLogJson;
    //   return data;
    // },
    // getDtcSnapshot: async ({ snapshotId }: { snapshotId: string }) => {
    //   const queryString = `{snapshotJson(snapshotId:"${snapshotId}")}`;
    //   const url = `bdip/unit-statuses-api/v1/graphql?query=${queryString}`;
    //   const response = await baseApiCall<{ data: { snapshotJson: string } }>(url);
    //   const data = JSON.parse(response.data.snapshotJson) as ISnapshotItem[];
    //   return data;
    // },
  },
  PowerSolutions: {
    getLatestOperatingHours: async ({
      endcustomerunitids,
      signal,
    }: {
      endcustomerunitids: number[];
      signal?: AbortSignal;
    }) => {
      const batchSize = 40;
      const batches: number[][] = [];

      // Split into batches of 40
      for (let i = 0; i < endcustomerunitids.length; i += batchSize) {
        batches.push(endcustomerunitids.slice(i, i + batchSize));
      }

      // Fetch all batches in parallel
      const requests = batches.map((batch) => {
        const url = `bdip/penta-power-solutions-api/v1/latest-operating-hours-by-endcustomerunitids?endcustomerunitids=${batch.join(',')}`;
        return baseApiCall<ILatestOperatingHours[]>(url, { signal });
      });

      const results = await Promise.all(requests);

      // Flatten and return combined results
      return results.flat();
    },
    getAssetPositions: async ({
      endCustomerUnitIds,
      year,
      month,
      signal,
    }: {
      endCustomerUnitIds: number[];
      year: string;
      month: string;
      signal?: AbortSignal;
    }) => {
      const requestId = uuidv4();
      const { startDate, endDate } = dates.getDateRange(year, month);

      const params: AssetPositionQueryParams = {
        requestId,
        pageNumber: 1,
        pageSize: 100,
        dateType: 'created',
        startTime: startDate,
        stopTime: endDate,
        unitIds: endCustomerUnitIds.join(','),
        latestOnly: true,
      };

      const queryString = common.buildQueryString(params);
      const url = `bdip/penta-power-solutions-api/v1/asset-position?${queryString}`;
      const response = await baseApiCall<IAssetPositionResponse>(url, { signal });

      const latestPositions = assetPositions.getLatestPositions(response);

      return {
        ...response,
        assetPositions: latestPositions,
        totalCount: latestPositions.length,
      };
    },
    getFuelConsumptionByMonth: async ({
      chassisIds,
      year,
      month,
      signal,
    }: {
      chassisIds: string;
      year: string | number;
      month: string;
      signal?: AbortSignal;
    }) => {
      const url = `bdip/penta-power-solutions-api/v1/fuel-consumption-calendar?chassisIds=${chassisIds}&timeWindow=MONTH&timeWindowValue=${
        month
      }&year=${year}&resolution=DAILY&offsetHours=1`;
      const response = await baseApiCall<IFuelConsumptionResponse>(url, { signal });
      return response;
    },
  },
  IndividualProducts: {
    getIndividualProducts: async ({ serialNumbers, signal }: { serialNumbers: string[]; signal?: AbortSignal }) => {
      const url = `systoken-bdip/individual-products-api/v1/individual-products?serialNumbers=${serialNumbers.join(',')}`;
      const requests = await baseApiCall<IIndividualProductsResponse>(url, { signal });
      return requests.data;
    },
  },
  Campaigns: {
    downloadCampaignsAsExcel: async ({ signal }: { signal?: AbortSignal } = {}) => {
      const url = `campaigns-excel`;
      const response = await baseApiCall<Blob>(url, { responseType: 'blob', signal });
      return response;
    },
    getAfterMarketCampaigns: async ({ serialNumbers, signal }: { serialNumbers: string[]; signal?: AbortSignal }) => {
      const url = `bdip/aftermarket-campaigns-api/v1.2/aftermarket-campaigns?concernedIndividualProductSerialNumbers=${serialNumbers.join(',')}&includePerformedCampaigns=true&includePerformedEcodeCampaigns=true`;
      const response = await baseApiCall<IAftermarketCampaignResponse>(url, { signal });
      return response;
    },
    getCampaignStatuses: async ({
      endCustomerUnitIds,
      signal,
    }: {
      endCustomerUnitIds: string[];
      signal?: AbortSignal;
    }) => {
      const requests = endCustomerUnitIds.map((id) =>
        baseApiCall<IInstallableCampaign[]>(`${CAMPAIGN_STATUS_REVERSE_PROXY_URI}/campaignstatus?unitId=${id}`, {
          signal,
        })
      );
      const results = await Promise.allSettled(requests);
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result) => result.value);
      return successfulResponses;
    },
    getCombinedCampaigns: async ({
      endCustomerUnitIds,
      serialNumbers,
      signal,
    }: {
      endCustomerUnitIds: string[];
      serialNumbers: string[];
      signal?: AbortSignal;
    }) => {
      const [afterMarketCampaigns, installableCampaigns] = await Promise.all([
        vpcService.Campaigns.getAfterMarketCampaigns({ serialNumbers, signal }),
        vpcService.Campaigns.getCampaignStatuses({ endCustomerUnitIds, signal }),
      ]);

      /* ───────────── group aftermarket campaigns ─────────── */
      const campaignsWithSerials: ICampaignWithSerialNumbers[] = [];
      afterMarketCampaigns.data.forEach((campaign) => {
        // Group by campaignId + chassisSeries + chassisNumber
        // This keeps different units separate even for same campaign
        const existing = campaignsWithSerials.find(
          (c) =>
            c.data.aftermarketCampaignId === campaign.aftermarketCampaignId &&
            c.data.chassisSeries === campaign.chassisSeries &&
            c.data.chassisNumber === campaign.chassisNumber
        );
        if (existing) {
          existing.serials.push(campaign.concernedIndividualProductSerialNumber);
        } else {
          campaignsWithSerials.push({
            data: { ...campaign }, // keep the object immutable
            serials: [campaign.concernedIndividualProductSerialNumber],
          });
        }
      });

      /* ─────── enrich with installable-campaign matches ───── */
      if (installableCampaigns.length) {
        for (const wrapper of campaignsWithSerials) {
          const match = installableCampaigns.find(
            (inst) =>
              inst.chassisSeries.toLowerCase().trim() === wrapper.data.chassisSeries.toLowerCase().trim() &&
              inst.chassisNumber.toLowerCase().trim() === wrapper.data.chassisNumber.toLowerCase().trim() &&
              inst.campaignCode.toLowerCase().trim() === wrapper.data.eCode?.toLowerCase().trim()
          );

          if (match) {
            wrapper.data.installableCampaign = match;

            // “upgrade” the campaign type
            switch (wrapper.data.aftermarketCampaignType) {
              case 'Recall':
                wrapper.data.aftermarketCampaignType = 'Recall_Installation';
                break;
              case 'Quality':
                wrapper.data.aftermarketCampaignType = 'Quality_Installation';
                break;
              case 'Service':
                wrapper.data.aftermarketCampaignType = 'Service_Installation';
                break;
              default:
                /* leave untouched */ break;
            }
          }
        }
      }

      return campaignsWithSerials;
    },
  },
  EndCustomer: {
    // The proxy resolves email/endCustomerId from the caller's own token — no params.
    getEndCustomer: async ({ signal }: { signal?: AbortSignal } = {}) => {
      const response = await baseApiCall<IApiResponse<IEndCustomer>>('end-customer', { signal });
      return response.data;
    },
  },
  Services: {
    getServices: async ({
      endCustomerUnitId,
      fleetAccountId,
      signal,
    }: {
      endCustomerUnitId: string;
      fleetAccountId: string;
      signal?: AbortSignal;
    }) => {
      const url = `/issb/services-status?unitIds=${endCustomerUnitId}&fleetAccountId=${fleetAccountId}`;
      const response = await baseApiCall<ITelemetryServiceResponse>(url, { signal });
      return response.data;
    },
    getLegalContractPdf: async ({ uri, signal }: { uri: string; signal?: AbortSignal }) => {
      const response = await baseApiCall<Blob>(`/issb/${uri}`, { responseType: 'blob', signal });
      return response;
    },
    activateService: async ({
      endCustomerUnitId,
      serviceId,
      fleetAccountId,
      formData,
      signal,
    }: {
      endCustomerUnitId: string;
      serviceId: number;
      fleetAccountId: string;
      formData: IServicesFormData;
      signal?: AbortSignal;
    }) => {
      const url = `/issb/services/active-monitoring/request-activate?unitId=${endCustomerUnitId}&serviceId=${serviceId}&fleetAccountId=${fleetAccountId}`;
      const response = await baseApiCall<ITelemetryServiceResponse>(url, {
        method: 'POST',
        data: formData,
        signal,
      });
      return response;
    },
    activateBatchServicess: async ({
      endCustomerUnitId,
      serviceIds,
      fleetAccountId,
      formData,
      signal,
    }: {
      endCustomerUnitId: string;
      serviceIds: number[];
      fleetAccountId: string;
      formData: IServicesFormData;
      signal?: AbortSignal;
    }) => {
      const url = `/issb/services/batch-activate?unitId=${endCustomerUnitId}&fleetAccountId=${fleetAccountId}`;
      const response = await baseApiCall<ITelemetryBatchServiceResponse>(url, {
        method: 'POST',
        data: {
          serviceIds: serviceIds,
          formData: formData,
        },
        signal,
      });
      return response;
    },
    activateMarineServices: async ({
      endCustomerUnitId,
      serviceIds,
      fleetAccountId,
      formData,
      signal,
    }: {
      endCustomerUnitId: string;
      serviceIds: number[];
      fleetAccountId: string;
      formData: IServicesFormData;
      signal?: AbortSignal;
    }) => {
      const url = `/issb/services/activate?unitId=${endCustomerUnitId}&fleetAccountId=${fleetAccountId}`;
      const response = await baseApiCall<ITelemetryBatchServiceResponse>(url, {
        method: 'POST',
        data: {
          serviceIds: serviceIds,
          formData: formData,
        },
        signal,
      });
      return response;
    },
    deactivateService: async ({
      endCustomerUnitId,
      individualServiceId,
      fleetAccountId,
      signal,
    }: {
      endCustomerUnitId: string;
      individualServiceId: number;
      fleetAccountId: string;
      signal?: AbortSignal;
    }) => {
      const url = `/issb/services/active-monitoring/request-deactivate?unitId=${endCustomerUnitId}&individualServiceId=${individualServiceId}&fleetAccountId=${fleetAccountId}`;
      const response = await baseApiCall<ITelemetryServiceResponse>(url, {
        method: 'POST',
        data: {},
        signal,
      });
      return response;
    },
    deactivateBatchServices: async ({
      endCustomerUnitId,
      serviceIds,
      fleetAccountId,
      signal,
    }: {
      endCustomerUnitId: string;
      serviceIds: number[];
      fleetAccountId: string;
      signal?: AbortSignal;
    }) => {
      const url = `/issb/services/batch-deactivate?unitId=${endCustomerUnitId}&fleetAccountId=${fleetAccountId}`;
      const response = await baseApiCall<ITelemetryBatchServiceResponse>(url, {
        method: 'POST',
        data: { serviceIds },
        signal,
      });
      return response;
    },
    deactivateMarineService: async ({
      endCustomerUnitId,
      individualServiceId,
      fleetAccountId,
      signal,
    }: {
      endCustomerUnitId: string;
      individualServiceId: number;
      fleetAccountId: string;
      signal?: AbortSignal;
    }) => {
      const url = `/issb/services/deactivate?unitId=${endCustomerUnitId}&individualServiceId=${individualServiceId}&fleetAccountId=${fleetAccountId}`;
      const response = await baseApiCall<ITelemetryServiceResponse>(url, {
        method: 'POST',
        data: {},
        signal,
      });
      return response;
    },
    getWifiStatusHistory: async ({
      chassisId,
      signal,
    }: {
      chassisId: string;
      signal?: AbortSignal;
    }): Promise<IWifiNetworkRow[]> => {
      const url = `tisp/${chassisId}/wifistatushistory`;
      const response = await baseApiCall<IWifiStatusResponseAndCorrelationId>(url, { signal });
      const latestStatus = response.wifiStatusHistoryResponse.wifiStatuses.find(
        (s) => s.wifiStatus.configuredWifiNetworks.length > 0
      );
      if (!latestStatus) return [];

      const { wifiStatus, receivedDateTime } = latestStatus;
      const connectedSsid = wifiStatus.connectedWifiNetwork?.ssid;

      return wifiStatus.configuredWifiNetworks
        .map((network) => ({
          ssid: network.ssid,
          wifiFrequency: network.wifiFrequency,
          priority: network.priority,
          signalStrength: network.signalStrength,
          connectionDateTime: network.connectionDateTime,
          isConnected: network.ssid === connectedSsid,
          receivedDateTime,
        }))
        .sort((a, b) => a.priority - b.priority);
    },
    updateWifiConfiguration: async ({
      chassisId,
      configurations,
      signal,
    }: {
      chassisId: string;
      configurations: IWifiConfigurationRequest[];
      signal?: AbortSignal;
    }) => {
      const url = `v2/wificonfig/${chassisId}`;
      const response = await baseApiCall<{ correlationId: string }>(url, {
        method: 'POST',
        data: configurations,
        signal,
      });
      return response;
    },
    deleteWifiConfiguration: async ({
      chassisId,
      ssid,
      signal,
    }: {
      chassisId: string;
      ssid: string;
      signal?: AbortSignal;
    }) => {
      const url = `wificonfig/${chassisId}/${encodeURIComponent(ssid)}`;
      const response = await baseApiCall<{ correlationId: string }>(url, {
        method: 'DELETE',
        signal,
      });
      return response;
    },
    getWifiConfig: async ({ chassisId, signal }: { chassisId: string; signal?: AbortSignal }) => {
      const url = `wificonfig/${chassisId}`;
      const response = await baseApiCall<{ correlationId: string }>(url, { signal });
      return response;
    },
    getWifiConfigByCorrelationId: async ({
      chassisId,
      correlationId,
      signal,
    }: {
      chassisId: string;
      correlationId: string;
      signal?: AbortSignal;
    }) => {
      const url = `wificonfig/${chassisId}/${correlationId}`;
      const response = await baseApiCall<IWifiConfigUpdateStatusResponse>(url, { signal });
      return response;
    },
    getWifiConfigUpdateStatus: async ({
      chassisId,
      correlationId,
      signal,
    }: {
      chassisId: string;
      correlationId: string;
      signal?: AbortSignal;
    }) => {
      const url = `wificonfigupdatestatus/${chassisId}/${correlationId}`;
      const response = await baseApiCall<IWifiConfigUpdateStatusSimple>(url, { signal });
      return response;
    },
    getWifiErrorHistory: async ({ chassisId, signal }: { chassisId: string; signal?: AbortSignal }) => {
      const url = `wifierrorhistory/${chassisId}`;
      const response = await baseApiCall<IWifiErrorHistoryResponse>(url, { signal });
      return response;
    },
    getServiceProtocols: async ({
      chassisIdentifiers,
      signal,
    }: {
      chassisIdentifiers: IChassisIdentifier[];
      signal?: AbortSignal;
    }) => {
      const requests = chassisIdentifiers.map((chassis) => {
        return baseApiCall<IServiceProtocolResponse>(
          `service-protocols-list?chassisSeries=${chassis.chassisSeries}&chassisNumber=${chassis.chassisNumber}&includeAttachment=true`,
          { signal }
        );
      });

      const results = await Promise.allSettled(requests);
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => (result as PromiseFulfilledResult<IServiceProtocolResponse>).value.data)
        .flat();

      // const response = _.sortBy(successfulResponses, 'serviceDate');
      return successfulResponses;
    },
    getServiceProtocolByKey: async ({ protocolKey, signal }: { protocolKey: string; signal?: AbortSignal }) => {
      const url = `service-protocols?id=${encodeURIComponent(protocolKey)}`;
      const response = await baseApiCall<IServiceProtocol>(url, { signal });
      return response;
    },
    getServiceProtocolAttachment: async ({ protocolKey, signal }: { protocolKey: string; signal?: AbortSignal }) => {
      const url = `bdip/service-protocols-api/v1/service-protocol-attachment/${encodeURIComponent(protocolKey)}`;
      const response = await baseApiCall<unknown>(url, { signal });
      return response;
    },
  },
  Invitations: {
    getInvitationById: async ({ invitationId, signal }: { invitationId: string; signal?: AbortSignal }) => {
      const url = getFleetApiUrl(`invitations/${invitationId}?confirmationToken=${123456}`);
      const response = await baseApiCall<IInvitation>(url, { signal });
      return response;
    },
    acceptInvitation: async ({
      invitationId,
      body,
      signal,
    }: {
      invitationId: string;
      body: IAcceptInvitationRequestBody;
      signal?: AbortSignal;
    }) => {
      const url = getFleetApiUrl(`invitations/${invitationId}/accept`);
      const response = await baseApiCall<unknown>(url, {
        method: 'POST',
        data: body,
        signal,
      });
      return response;
    },
    getInvitations: async ({
      fleetAccountId,
      status,
      pageNumber,
      pageSize,
      signal,
    }: {
      fleetAccountId: string;
      status: InvitationStatusType;
      pageNumber: number;
      pageSize: number;
      signal?: AbortSignal;
    }) => {
      const url = getFleetApiUrl(
        `/fleet-accounts/${fleetAccountId}/invitations?pageNumber=${pageNumber}&pageSize=${pageSize}&status=${status}`
      );
      const response = await baseApiCall<IFleetInvitationResponse>(url, { signal });
      return response;
    },
    createInvitation: async ({ fleetAccountId, signal }: { fleetAccountId: string; signal?: AbortSignal }) => {
      const url = getFleetApiUrl(`/fleet-accounts/${fleetAccountId}/invitations/${uuidv4()}`);
      const response = await baseApiCall<IFleetInvitationResponse>(url, {
        method: 'POST',
        data: {
          inviteeEmail: 'placeholder@vpconnect.com', // Placeholder, actual email will be sent by VPC backend
          roles: ['Member'],
        },
        signal,
      });
      return response;
    },
    cancelInvitation: async ({
      fleetAccountId,
      invitationId,
      signal,
    }: {
      fleetAccountId: string;
      invitationId: string;
      signal?: AbortSignal;
    }) => {
      const url = getFleetApiUrl(`/fleet-accounts/${fleetAccountId}/invitations/${invitationId}/cancel`);
      const response = await baseApiCall<IFleetInvitationResponse>(url, {
        method: 'POST',
        data: {
          entityVersion: '1',
        },
        signal,
      });
      return response;
    },
  },
  Fleet: {
    getAvailableFleetRoles: async () => {
      const url = getFleetApiUrl(`fleets/roles`);
      const response = await baseApiCall<string[]>(url);
      return response;
    },
    getFleetRoles: async ({ fleetId, signal }: { fleetId: string; signal?: AbortSignal }) => {
      const pageSize = 100;
      const url = getFleetApiUrl(`fleets/${fleetId}/roles?pageNumber=1&pageSize=${pageSize}`, 'v2');
      const firstResponse = await baseApiCall<IFleetRolesResponse>(url, { signal });

      // If there's only one page, return immediately
      if (firstResponse.totalPages <= 1) {
        return firstResponse;
      }

      // Fetch remaining pages in parallel
      const remainingPages = Array.from({ length: firstResponse.totalPages - 1 }, (_, i) => i + 2);

      const remainingRequests = remainingPages.map((pageNumber) => {
        const pageUrl = getFleetApiUrl(`fleets/${fleetId}/roles?pageNumber=${pageNumber}&pageSize=${pageSize}`, 'v2');
        return baseApiCall<IFleetRolesResponse>(pageUrl, { signal });
      });

      const remainingResponses = await Promise.all(remainingRequests);

      // Combine all items
      const allItems = [...firstResponse.items, ...remainingResponses.flatMap((r) => r.items)];

      return {
        items: allItems,
        pageNumber: 1,
        pageSize: allItems.length,
        totalItems: firstResponse.totalItems,
        totalPages: 1,
      };
    },
    assignFleetRoles: async ({
      fleetId,
      endUserId,
      roles,
      signal,
    }: {
      fleetId: string;
      endUserId: string;
      roles: string[];
      signal?: AbortSignal;
    }) => {
      const promises = roles.map((role) => {
        const url = getFleetApiUrl(`fleets/${fleetId}/roles/${uuidv4()}`);
        return baseApiCall<number>(url, {
          method: 'POST',
          data: { endCustomerId: endUserId, role },
          signal,
        });
      });
      const results = await Promise.allSettled(promises);
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result) => (result as PromiseFulfilledResult<number>).value);
      return successfulResponses;
    },
    removeFleetRoles: async ({
      fleetId,
      roles,
      signal,
    }: {
      fleetId: string;
      roles: IFleetRole[];
      signal?: AbortSignal;
    }) => {
      const promises = roles.map((role) => {
        const url = getFleetApiUrl(`fleets/${fleetId}/roles/${role.fleetRoleId}?entityVersion=${role.entityVersion}`);
        return baseApiCall<number>(url, {
          method: 'DELETE',
          signal,
        });
      });
      const results = await Promise.allSettled(promises);
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result) => (result as PromiseFulfilledResult<number>).value);
      return successfulResponses;
    },
    getFleets: async ({ fleetAccountId, signal }: { fleetAccountId: string; signal?: AbortSignal }) => {
      const url = getFleetApiUrl(`fleet-accounts/${fleetAccountId}/fleets`);
      const response = await baseApiCall<IFleet[]>(url, { signal });
      return response.filter((fleet) => fleet.type === 'Custom' && !isFleetPendingDeletion(fleet.fleetId));
    },
    getFleetUnits: async ({ fleetId, signal }: { fleetId: string; signal?: AbortSignal }) => {
      const pageSize = 100;
      const url = getFleetApiUrl(`fleets/${fleetId}/units?pageNumber=1&pageSize=${pageSize}`);
      const firstResponse = await baseApiCall<IFleetUnitsResponse>(url, { signal });

      // If there's only one page, return immediately
      if (firstResponse.totalPages <= 1) {
        return withoutPendingUnitRemovals(fleetId, firstResponse);
      }

      // Fetch remaining pages in parallel
      const remainingPages = Array.from({ length: firstResponse.totalPages - 1 }, (_, i) => i + 2);

      const remainingRequests = remainingPages.map((pageNumber) => {
        const pageUrl = getFleetApiUrl(`fleets/${fleetId}/units?pageNumber=${pageNumber}&pageSize=${pageSize}`);
        return baseApiCall<IFleetUnitsResponse>(pageUrl, { signal });
      });

      const remainingResponses = await Promise.all(remainingRequests);

      // Combine all items
      const allItems = [...firstResponse.items, ...remainingResponses.flatMap((r) => r.items)];

      return withoutPendingUnitRemovals(fleetId, {
        items: allItems,
        pageNumber: 1,
        pageSize: allItems.length,
        totalItems: firstResponse.totalItems,
        totalPages: 1,
      });
    },
    getFleetsUnits: async ({ fleetAccountId, signal }: { fleetAccountId: string; signal?: AbortSignal }) => {
      const fleets = await vpcService.Fleet.getFleets({ fleetAccountId, signal });
      const requests = fleets.map((fleet) =>
        vpcService.Fleet.getFleetUnits({ fleetId: fleet.fleetId, signal }).then((response) => ({
          fleet,
          response,
        }))
      );

      const results = await Promise.allSettled(requests);
      const unitFleetMap = new Map<string, IFleetUnit & { fleets: IFleet[] }>();

      results
        .filter((result) => result.status === 'fulfilled')
        .forEach((result) => {
          const { fleet, response } = (
            result as PromiseFulfilledResult<{ fleet: IFleet; response: IFleetUnitsResponse }>
          ).value;
          response.items.forEach((unit) => {
            const existing = unitFleetMap.get(unit.endCustomerUnitId);
            if (existing) {
              existing.fleets.push(fleet);
            } else {
              unitFleetMap.set(unit.endCustomerUnitId, { ...unit, fleets: [fleet] });
            }
          });
        });
      const response = Array.from(unitFleetMap.values());
      return response;
    },
    getFleetUnitsCount: async ({ fleetId, signal }: { fleetId: string; signal?: AbortSignal }): Promise<number> => {
      // The API's count may still include units we just removed, so count the filtered list instead
      if (getPendingUnitRemovals(fleetId).size > 0) {
        const { items } = await vpcService.Fleet.getFleetUnits({ fleetId, signal });
        return items.length;
      }
      const url = getFleetApiUrl(`fleets/${fleetId}/units?pageNumber=1&pageSize=1`);
      const response = await baseApiCall<IFleetUnitsResponse>(url, { signal });
      return response.totalItems;
    },
    createFleet: async (props: { fleetAccountId: string; name: string; signal?: AbortSignal }) => {
      // Generated once so retries target the same fleet instead of creating duplicates
      const fleetId = uuidv4();
      const url = getFleetApiUrl(`fleet-accounts/${props.fleetAccountId}/fleets/${fleetId}`);
      const response = await common.withRetry(
        () =>
          baseApiCall<number>(url, {
            method: 'POST',
            data: {
              name: props.name,
            },
            signal: props.signal,
          }),
        { delayMs: FLEET_API_CONSISTENCY_DELAY_MS }
      );
      await common.wait(FLEET_API_CONSISTENCY_DELAY_MS);
      return { response, fleetId };
    },
    deleteFleet: async ({
      fleetAccountId,
      fleetId,
      entityVersion,
      signal,
    }: {
      fleetAccountId: string;
      fleetId: string;
      entityVersion: number;
      signal?: AbortSignal;
    }) => {
      // First we need to remove all the units from the fleet
      const fleetUnits = await vpcService.Fleet.getFleetUnits({
        fleetId,
      });
      if (fleetUnits.items.length > 0) {
        await vpcService.Fleet.removeUnitsFromFleet({
          fleetId,
          fleetUnits: fleetUnits.items.map((unit) => ({
            endCustomerUnitId: unit.endCustomerUnitId,
            entityVersion: unit.entityVersion,
          })),
          signal,
        });
      }

      // Now we can delete the fleet
      const url = getFleetApiUrl(`fleet-accounts/${fleetAccountId}/fleets/${fleetId}?entityVersion=${entityVersion}`);
      const response = await common.withRetry(
        () =>
          baseApiCall<number>(url, {
            method: 'DELETE',
            signal,
          }),
        { delayMs: FLEET_API_CONSISTENCY_DELAY_MS }
      );
      pendingFleetDeletions.set(fleetId, Date.now() + PENDING_DELETE_TTL_MS);
      return response;
    },
    editFleet: async (props: {
      fleetAccountId: string;
      fleetId: string;
      name: string;
      entityVersion: number;
      signal?: AbortSignal;
    }) => {
      const url = getFleetApiUrl(`fleet-accounts/${props.fleetAccountId}/fleets/${props.fleetId}`);
      const response = await common.withRetry(
        () =>
          baseApiCall<number>(url, {
            method: 'PUT',
            data: {
              name: props.name,
              entityVersion: props.entityVersion,
            },
            signal: props.signal,
          }),
        { delayMs: FLEET_API_CONSISTENCY_DELAY_MS }
      );
      await common.wait(FLEET_API_CONSISTENCY_DELAY_MS);
      return response;
    },
    addUnitsToFleet: async (props: { fleetId: string; endCustomerUnitIds: string[]; signal?: AbortSignal }) => {
      const requests = props.endCustomerUnitIds.map((unitId) => {
        const url = getFleetApiUrl(`fleets/${props.fleetId}/units/${unitId}`);
        // Retried per unit, e.g. when a just-created fleet isn't visible to the API yet
        return common.withRetry(
          () =>
            baseApiCall<number>(url, {
              method: 'POST',
              signal: props.signal,
            }),
          { delayMs: FLEET_API_CONSISTENCY_DELAY_MS }
        );
      });
      const results = await Promise.allSettled(requests);
      logFailedUnits('add', props.fleetId, props.endCustomerUnitIds, results);
      const addedUnitIds = props.endCustomerUnitIds.filter((_, i) => results[i].status === 'fulfilled');
      unmarkUnitsRemoved(props.fleetId, addedUnitIds.map(String));
      if (addedUnitIds.length > 0) {
        await waitForFleetUnits(
          props.fleetId,
          (unitIds) => addedUnitIds.every((id) => unitIds.has(String(id))),
          props.signal
        );
      }
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => (result as PromiseFulfilledResult<number>).value);
      return successfulResponses;
    },
    removeUnitsFromFleet: async ({
      fleetId,
      fleetUnits,
      signal,
    }: {
      fleetId: string;
      fleetUnits: { endCustomerUnitId: string; entityVersion: number }[];
      signal?: AbortSignal;
    }) => {
      const deleteUnit = (unit: { endCustomerUnitId: string; entityVersion: number }) =>
        baseApiCall<number>(
          getFleetApiUrl(`fleets/${fleetId}/units/${unit.endCustomerUnitId}?entityVersion=${unit.entityVersion}`),
          { method: 'DELETE', signal }
        );

      const removedUnitIds: string[] = [];
      const lastErrors = new Map<string, unknown>();
      let pending = fleetUnits.map((unit) => ({ ...unit, endCustomerUnitId: String(unit.endCustomerUnitId) }));

      for (let attempt = 1; attempt <= FLEET_API_WRITE_ATTEMPTS && pending.length > 0; attempt++) {
        if (attempt > 1) {
          await common.wait(FLEET_API_CONSISTENCY_DELAY_MS);
          // Retry with the current entityVersion; the API rejects a stale one, and the version
          // can change shortly after a unit is added to the fleet
          try {
            const { items } = await vpcService.Fleet.getFleetUnits({ fleetId, signal });
            const versions = new Map(items.map((unit) => [String(unit.endCustomerUnitId), unit.entityVersion]));
            pending.forEach((unit) => {
              if (!versions.has(unit.endCustomerUnitId)) removedUnitIds.push(unit.endCustomerUnitId);
            });
            pending = pending
              .filter((unit) => versions.has(unit.endCustomerUnitId))
              .map((unit) => ({ ...unit, entityVersion: versions.get(unit.endCustomerUnitId)! }));
          } catch {
            // Retry with the versions we already have
          }
        }

        const attemptUnits = pending;
        const results = await Promise.allSettled(attemptUnits.map(deleteUnit));
        pending = [];
        results.forEach((result, i) => {
          const unit = attemptUnits[i];
          if (result.status === 'fulfilled') {
            removedUnitIds.push(unit.endCustomerUnitId);
          } else {
            lastErrors.set(unit.endCustomerUnitId, result.reason);
            pending.push(unit);
          }
        });
      }

      pending.forEach((unit) =>
        console.error(
          `Failed to remove unit ${unit.endCustomerUnitId} (fleet ${fleetId})`,
          lastErrors.get(unit.endCustomerUnitId)
        )
      );
      markUnitsRemoved(fleetId, removedUnitIds);
      return removedUnitIds;
    },
  },
  WebToNative: {
    bleSalt: async ({
      chassisSeries,
      chassisNumber,
      signal,
    }: {
      chassisSeries: string;
      chassisNumber: string;
      signal?: AbortSignal;
    }) => {
      const url = `bdip/${USE_FLEET_PROD ? 'prod/' : ''}shortrangeble-api/blesalt?chassisSeries=${chassisSeries}&chassisNumber=${chassisNumber}`;
      const response = await baseApiCall<{ bleSalt: string }>(url, { signal });
      return response;
    },
    bleToken: async ({
      chassisSeries,
      chassisNumber,
      signal,
    }: {
      chassisSeries: string;
      chassisNumber: string;
      signal?: AbortSignal;
    }) => {
      const url = `bdip/${USE_FLEET_PROD ? 'prod/' : ''}shortrangeble-api/bletoken?chassisSeries=${chassisSeries}&chassisNumber=${chassisNumber}`;
      const response = await baseApiCall<{
        token: {
          access_token: string;
          issued_token_type: string;
          token_type: string;
        };
        expiryTimeStamp: number;
      }>(url, { signal });
      return response;
    },
    registerDevice: async ({ request, signal }: { request: TRegisterDeviceRequest; signal?: AbortSignal }) => {
      const url = `bdip/notification-devices-api/v1/client-applications`;
      const response = await baseApiCall<TRegisterDeviceResponse>(url, {
        method: 'POST',
        data: request,
        signal,
      });
      return response;
    },
  },
  Publications: {
    getHandbooks: async ({
      endCustomerUnitId,
      serialNumbers,
      signal,
    }: {
      endCustomerUnitId: string;
      serialNumbers?: string[];
      signal?: AbortSignal;
    }) => {
      let url = `handbooks/${endCustomerUnitId}`;
      if (serialNumbers && serialNumbers.length > 0) {
        url += `?serialNumbers=${serialNumbers.join(',')}`;
      }
      const response = await baseApiCall<IPublicationResponse>(url, { signal });
      return response;
    },
    getPublicationMetadata: async ({ metadataUrl, signal }: { metadataUrl: string; signal?: AbortSignal }) => {
      // Prepend appropriate prefix if not already present
      const url = metadataUrl.startsWith('systoken-bdip/') ? metadataUrl : `systoken-bdip/${metadataUrl}`;

      const response = await baseApiCall<IPublicationMetadata>(url, { signal });
      return response;
    },
    getPublicationBinary: async ({ binaryUrl, signal }: { binaryUrl: string; signal?: AbortSignal }) => {
      const url = binaryUrl.startsWith('systoken-bdip/') ? binaryUrl : `systoken-bdip/${binaryUrl}`;
      const response = await baseApiCall<AxiosResponse<Blob>>(url, { responseType: 'blob', signal }, true);

      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const contentDisposition = response.headers['content-disposition'];
      let fileName: string | undefined;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      return {
        blob: response.data,
        contentType: String(contentType),
        fileName,
      };
    },
  },
  Geocoding: {
    placeById: async ({ placeId }: { placeId: string }) => {
      const url = `https://api.geoapify.com/v2/place-details?id=${encodeURIComponent(placeId)}&format=json&apiKey=4b360fe95e6b4628901cb276204bfe80`;
      const response = await axios.get<IPlaceDetailsResponse>(url);
      return response.data;
    },
    autocomplete: async ({ input, countrycode }: { input: string; countrycode: string }) => {
      const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(input)}&limit=5&bias=countrycode:${countrycode}&format=json&apiKey=4b360fe95e6b4628901cb276204bfe80`;
      const response = await axios.get<IGeocodingResponse>(url);
      return response.data;
    },
  },
};
